import React, { useState, useEffect } from 'react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Phone, KeyRound, Eye, EyeOff, AlertCircle, CheckCircle } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { API_ENDPOINTS } from '../config/api';

const ForgotPasswordPage = () => {
  const [step, setStep] = useState('identify'); // 'identify' | 'otp' | 'done'
  const [method, setMethod] = useState('username'); // 'username' | 'phone'
  const [username, setUsername] = useState('');
  const [phone, setPhone] = useState('');
  const [otp, setOtp] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [countdown, setCountdown] = useState(0);
  const [failCount, setFailCount] = useState(0);
  const [maskedPhone, setMaskedPhone] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    if (countdown <= 0) return;
    const t = setTimeout(() => setCountdown(c => c - 1), 1000);
    return () => clearTimeout(t);
  }, [countdown]);

  const fullPhone = phone.length === 10 ? `+91${phone}` : phone;

  const handleSendOTP = async (e) => {
    if (e) e.preventDefault();
    setError('');
    if (method === 'username' && !username.trim()) { setError('Enter your username'); return; }
    if (method === 'phone' && phone.replace(/\D/g, '').length < 10) { setError('Enter a valid 10-digit mobile number'); return; }
    setIsLoading(true);
    try {
      const body = { purpose: 'password_reset' };
      if (method === 'username') body.username = username.trim();
      else body.phone = fullPhone;
      const res = await fetch(API_ENDPOINTS.SEND_OTP, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
      let data; try { data = await res.json(); } catch { data = {}; }
      if (res.ok && data.success !== false) {
        setMaskedPhone(data.data?.masked_phone || data.masked_phone || (method === 'phone' ? `+91${phone.slice(0,2)}****${phone.slice(-2)}` : ''));
        setStep('otp'); setCountdown(60); setError('');
      } else {
        const nc = failCount + 1; setFailCount(nc);
        setError(data?.message || (method === 'username' ? 'Username not found' : 'Phone not registered'));
      }
    } catch { setError('Network error. Please try again.'); }
    finally { setIsLoading(false); }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setError('');
    if (otp.length !== 6) { setError('Enter 6-digit OTP'); return; }
    if (newPassword.length < 6) { setError('Password must be at least 6 characters'); return; }
    if (newPassword !== confirmPassword) { setError('Passwords do not match'); return; }

    setIsLoading(true);
    try {
      const res = await fetch(API_ENDPOINTS.RESET_PASSWORD, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone: method === 'phone' ? fullPhone : undefined, username: method === 'username' ? username : undefined, otp, new_password: newPassword }),
      });
      let data;
      try { data = await res.json(); } catch { data = {}; }
      if (res.ok) {
        setStep('done');
        setSuccess('Password reset successfully! Redirecting to login...');
        setTimeout(() => navigate('/login'), 2500);
      } else {
        setError(data?.message || 'Reset failed. Check OTP and try again.');
      }
    } catch { setError('Network error. Please try again.'); }
    finally { setIsLoading(false); }
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-start bg-gradient-to-b from-lotus-light to-lotus-darker px-4 py-8">
      <div className="mb-6 text-center flex justify-center">
        <img src="/logo.png" alt="Reddy Anna Book" className="h-10 sm:h-14" style={{ objectFit: 'contain' }} />
      </div>

      <div className="w-full max-w-md bg-white border border-gray-200 rounded-lg shadow-sm p-6 space-y-5">
        <h1 className="text-center text-xl font-bold">
          {step === 'done' ? '✅ Password Reset' : '🔒 Forgot Password'}
        </h1>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-center gap-2">
            <AlertCircle className="w-4 h-4 text-red-500 shrink-0" />
            <span className="text-sm text-red-600">{error}</span>
          </div>
        )}
        {success && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-3 flex items-center gap-2">
            <CheckCircle className="w-4 h-4 text-green-500 shrink-0" />
            <span className="text-sm text-green-600">{success}</span>
          </div>
        )}

        {step === 'identify' && (
          <form onSubmit={handleSendOTP} className="space-y-4">
            {method === 'username' ? (
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 block">Username</label>
                <Input type="text" placeholder="Enter your username" value={username} onChange={e => setUsername(e.target.value)} className="h-11" />
                <p className="text-xs text-gray-500 mt-1">OTP will be sent to your registered WhatsApp</p>
              </div>
            ) : (
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 block">WhatsApp Number</label>
                <div className="flex gap-2">
                  <div className="flex items-center bg-gray-100 border rounded-md px-3 text-sm font-medium text-gray-600">+91</div>
                  <div className="relative flex-1">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input type="tel" placeholder="Enter 10-digit mobile" maxLength={10} value={phone} onChange={e => setPhone(e.target.value.replace(/\D/g, ''))} className="pl-10 h-11" />
                  </div>
                </div>
              </div>
            )}
            {failCount >= 2 && method === 'username' && (
              <button type="button" onClick={() => { setMethod('phone'); setError(''); }} className="w-full py-2 text-sm text-green-600 border border-green-400 rounded-lg hover:bg-green-50">📱 Try with WhatsApp number instead</button>
            )}
            {method === 'phone' && (
              <button type="button" onClick={() => { setMethod('username'); setError(''); setFailCount(0); }} className="text-sm text-gray-500 hover:underline">← Use username instead</button>
            )}
            <Button type="submit" className="w-full bg-green-600 hover:bg-green-700 h-11 text-white font-semibold" disabled={isLoading}>
              {isLoading ? 'Sending OTP...' : '📲 Send OTP via WhatsApp'}
            </Button>
          </form>
        )}

        {step === 'otp' && (
          <form onSubmit={handleResetPassword} className="space-y-4">
            <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-center">
              <p className="text-sm text-green-700">OTP sent to <b>{maskedPhone || `+91 ${phone}`}</b> on WhatsApp</p>
              {countdown > 0 && <p className="text-xs text-green-600 mt-1">Expires in {countdown}s</p>}
            </div>

            <div>
              <label className="text-sm font-medium text-gray-700 mb-1 block">Enter OTP</label>
              <Input type="text" placeholder="6-digit OTP" maxLength={6}
                value={otp} onChange={e => setOtp(e.target.value.replace(/\D/g, ''))}
                className="text-center text-lg tracking-[0.5em] font-bold" />
            </div>

            <div>
              <label className="text-sm font-medium text-gray-700 mb-1 block">New Password</label>
              <div className="relative">
                <KeyRound className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input type={showPassword ? 'text' : 'password'} placeholder="New password (min 6 chars)"
                  value={newPassword} onChange={e => setNewPassword(e.target.value)} className="pl-10 pr-10" />
                <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2">
                  {showPassword ? <EyeOff className="w-4 h-4 text-gray-400" /> : <Eye className="w-4 h-4 text-gray-400" />}
                </button>
              </div>
            </div>

            <div>
              <label className="text-sm font-medium text-gray-700 mb-1 block">Confirm Password</label>
              <Input type="password" placeholder="Confirm new password"
                value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} className="pl-3" />
            </div>

            <Button type="submit" className="w-full bg-lotus hover:bg-lotus-dark h-11 text-white font-semibold" disabled={isLoading}>
              {isLoading ? 'Resetting...' : '🔒 Reset Password'}
            </Button>

            {countdown <= 0 && (
              <button type="button" onClick={handleSendOTP} className="w-full text-sm text-blue-600 hover:underline">
                Resend OTP
              </button>
            )}
          </form>
        )}

        {step === 'done' && (
          <div className="text-center py-4">
            <div className="text-4xl mb-3">✅</div>
            <p className="text-gray-600">Redirecting to login...</p>
          </div>
        )}

        <div className="text-center text-sm text-gray-500">
          <Link to="/login" className="text-blue-600 hover:underline">← Back to Login</Link>
        </div>
      </div>
    </div>
  );
};

export default ForgotPasswordPage;
